import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { set, isArray, cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import moment from 'moment';
import type { ValueGetterParams } from 'ag-grid-community';
import { useProductCostingState } from '../ProductCostingContext';
import { landedInfoFieldPath } from '../fieldPath';
import { resolveDcDefaultCurrValue } from '../exchange-rate.helpers';
import type { ExchangeRateMasterEntry } from '../exchange-rate.helpers';
import { checkCurrency, shortenStringWithTooltip, roundOff } from '../../../../Components/helper';
import useShowHideSectionHooks from '../Hooks/useShowHideSectionHooks';
import useDetailedPageHook from '../Hooks/useDetailedPageHook';
import useRefreshHooks from '../Hooks/useRefreshHooks';
import useDetailedCostingHooks from '../Hooks/useDetailedCostingHooks';
import { BombiixAGGrid, useDeferredRowChange } from '../../../../Components/BombiixAGGrid';
import type { BombiixCellRendererParams, BombiixColumnDef, BombiixFillBatchItem, BombiixRowId } from '../../../../Components/BombiixAGGrid/types';
import type { DistributionCentre, LandedInfoStateEntry } from '../types';
import { useLengthLabel } from '../../../../hooks/useLengthLabel';
import { CollapseIcon, ExpandIcon, InactiveIcon, InfoIcon } from '../../../../bombiix-icon';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import { FREIGHT_AUTOMATED_ALIAS } from '../automated-freight.helpers';

/** Inline marker glyph; smaller than the 24px toolbar standard so it sits inside a grid cell. */
const ICON_SIZE_SM = 20;

/** Card-header glyph; the icon-container primitive is a 20px box with a 16px glyph. */
const ICON_SIZE_CARD = 16;

/** One dynamic landed column value on a row. */
interface LandedInfoCellValue {
  tempValue?: unknown;
  calculatedValue?: unknown;
  [key: string]: unknown;
}

/**
 * One landed row as this table reads it. The template adds an arbitrary `<aliasName>` /
 * `<aliasName>CalculatedValue` pair per column, hence the index signature.
 */
interface LandedDataRow {
  colourWay?: { name?: string } | undefined;
  colourway?: { name?: string } | undefined;
  size?: string | undefined;
  length?: { name?: string } | undefined;
  sizeRange?: string[] | undefined;
  lengthRange?: Array<{ name?: string }> | undefined;
  landedInfo?: LandedInfoCellValue[] | undefined;
  freightInputEnabled?: boolean | undefined;
  [key: string]: unknown;
}

/** A template column definition, keyed by `aliasName`. */
interface LandedTemplateColumn {
  _id?: string;
  type?: string;
  title?: string;
  aliasName?: string;
  aliasDisplayName?: string;
  isActive?: boolean;
  isDefault?: boolean;
  isEditable?: boolean;
  [key: string]: unknown;
}

/** The costing template as this table reads it. */
interface CostingTemplateShape {
  _id?: string;
  topColumnArr?: LandedTemplateColumn[];
  defaultColumns?: LandedTemplateColumn[];
  bottomColumnArr?: LandedTemplateColumn[];
  landedInfo?: LandedTemplateColumn[];
  [key: string]: unknown;
}

/** The redux slices this table reads. */
interface LandedRootState {
  newProductCostingData: {
    costingTemplateDetail?: CostingTemplateShape;
    productExchnageRate?: { exchangeRateList?: ExchangeRateMasterEntry[]; defaultCurrencyValue?: string | number };
    isRefreshed?: boolean;
    productCostingDetail?: { colourWayToggle?: boolean; sizeLengthToggle?: boolean } | null;
  };
  UserDetails: { userInfo?: { companyCurrency?: string }; isSupplierLoggedIn?: boolean };
  SystemMastersDetails: { subBrandList?: Array<{ _id?: string; currency?: string }> };
  BOMConfigDetails: { productDetails?: { subBrandId?: { _id?: string } | string } };
}

/**
 * One rendered row. Declared with `type` rather than `interface`: BombiixAGGrid constrains its row
 * generic to `Record<string, unknown> & {…}`, and only type aliases receive the implicit index
 * signature. `data` carries the whole landed row, which every column accessor reads through.
 */
type LandedGridRow = {
  __cagId: string;
  data?: LandedDataRow | undefined;
  distributionName?: string | undefined;
  colourway?: string | undefined;
  size?: React.ReactNode | undefined;
  length?: React.ReactNode | undefined;
  /* AG Grid's fill handle writes the dragged column's own field key onto the row, which
     `fillGetter` then reads back; the index signature is what makes those dynamic
     `landedInfo_*` / `default_*` / `bottom_*` field names valid column paths. */
  [key: string]: unknown;
};

type LandedCellParams = BombiixCellRendererParams<LandedGridRow> & {
  accessor?: (row: LandedGridRow | undefined) => unknown;
};

/** Read-only cell that renders whatever the accessor returns, including JSX fragments. */
function LandedNodeCell(params: LandedCellParams): React.JSX.Element {
  const { data, accessor } = params;
  const content = typeof accessor === 'function' ? accessor(data) : params.value;
  const isEmptyContent = content === undefined || content === null || content === '';
  return (
    <div className="cag-cell cag-cell-readonly">
      <span>{isEmptyContent ? '' : (content as React.ReactNode)}</span>
    </div>
  );
}

type LandedAmountCellParams = LandedCellParams & { narrow?: boolean };

/** Currency-prefixed editable amount cell. */
function LandedAmountInputCell(params: LandedAmountCellParams): React.JSX.Element {
  const { data, colDef, onRowChange, getRowId, currencySymbol = '£', disabled, accessor, narrow } = params;
  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;
  const value = typeof accessor === 'function' ? accessor(data) : params.value;

  const focusedRef = useRef<boolean>(false);
  const [local, setLocal] = useState<string>((value ?? '') as string);
  const [commitChange, flushChange] = useDeferredRowChange(onRowChange);
  useEffect(() => {
    if (!focusedRef.current) setLocal((value ?? '') as string);
  }, [value]);

  return (
    <div className="cag-cell">
      <div className={narrow ? 'w-80px flex-shrink-0' : 'w-100'}>
        <div className="cag-affix-field">
          <span className="cag-affix cag-affix-prefix force-ltr">{currencySymbol}</span>
          <input
            className="cag-affix-input"
            type="text"
            inputMode="decimal"
            value={local}
            disabled={disabled}
            onFocus={() => {
              focusedRef.current = true;
            }}
            onBlur={() => {
              focusedRef.current = false;
              flushChange();
            }}
            onChange={(event) => {
              const cleaned = event.target.value.replace(/[^0-9.\-]/g, '');
              setLocal(cleaned);
              commitChange(rowId as BombiixRowId, field as string, cleaned);
            }}
          />
        </div>
      </div>
    </div>
  );
}

type LandedFreightCellParams = LandedCellParams & {
  freightEnabledAccessor?: (row: LandedGridRow | undefined) => unknown;
  toggleDisabled?: boolean;
  dcIndex?: number;
};

/** Freight cell: an enable toggle paired with the amount input it gates. */
function LandedFreightAmountCell(params: LandedFreightCellParams): React.JSX.Element {
  const { data, colDef, onRowChange, getRowId, currencySymbol = '£', accessor, freightEnabledAccessor, toggleDisabled, dcIndex } = params;
  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;
  const value = typeof accessor === 'function' ? accessor(data) : params.value;
  const freightEnabled = typeof freightEnabledAccessor === 'function' ? !!freightEnabledAccessor(data) : false;

  const focusedRef = useRef<boolean>(false);
  const [local, setLocal] = useState<string>((value ?? '') as string);
  const [localEnabled, setLocalEnabled] = useState<boolean>(freightEnabled);
  const [commitChange, flushChange] = useDeferredRowChange(onRowChange);
  useEffect(() => {
    if (!focusedRef.current) setLocal((value ?? '') as string);
  }, [value]);
  useEffect(() => {
    setLocalEnabled(freightEnabled);
  }, [freightEnabled]);

  const amountDisabled = toggleDisabled || !localEnabled;

  return (
    <div className="cag-cell d-flex align-items-center">
      <div className={`landed-freight-cell${localEnabled ? ' landed-freight-cell--active' : ''}`}>
        <div className="design-switch d-flex align-items-center flex-shrink-0">
          <input
            type="checkbox"
            className="form-check-input mt-0"
            id={`freight_toggle_${dcIndex}_${rowId}`}
            checked={localEnabled}
            disabled={toggleDisabled}
            onChange={(event) => {
              setLocalEnabled(event.target.checked);
              onRowChange?.(rowId as BombiixRowId, 'freightInputEnabled', event.target.checked);
            }}
          />
        </div>
        <div className="w-60px flex-shrink-0 ml-2">
          <div className={`cag-affix-field${amountDisabled ? ' cag-affix-field-disabled' : ''}`}>
            <span className="cag-affix cag-affix-prefix force-ltr">{currencySymbol}</span>
            <input
              className="cag-affix-input"
              type="text"
              inputMode="decimal"
              value={local}
              disabled={amountDisabled}
              onFocus={() => {
                focusedRef.current = true;
              }}
              onBlur={() => {
                focusedRef.current = false;
                flushChange();
              }}
              onChange={(event) => {
                const cleaned = event.target.value.replace(/[^0-9.\-]/g, '');
                setLocal(cleaned);
                commitChange(rowId as BombiixRowId, field as string, cleaned);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

type LandedPercentCellParams = LandedCellParams & { calcAccessor?: (row: LandedGridRow | undefined) => unknown };

/** Percentage cell, with the money figure that percentage resolves to shown beside it. */
function LandedPercentInputCell(params: LandedPercentCellParams): React.JSX.Element {
  const { data, colDef, onRowChange, getRowId, currencySymbol = '£', disabled, accessor, calcAccessor } = params;
  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;
  const value = typeof accessor === 'function' ? accessor(data) : params.value;
  const calc = typeof calcAccessor === 'function' ? calcAccessor(data) : undefined;

  const focusedRef = useRef<boolean>(false);
  const [local, setLocal] = useState<string>((value ?? '') as string);
  const [commitChange, flushChange] = useDeferredRowChange(onRowChange);
  useEffect(() => {
    if (!focusedRef.current) setLocal((value ?? '') as string);
  }, [value]);

  return (
    <div className="cag-cell">
      <div className={`cag-affix-field w-80px flex-shrink-0${disabled ? ' cag-affix-field-disabled' : ''}`}>
        <input
          className="cag-affix-input"
          type="text"
          inputMode="decimal"
          value={local}
          disabled={disabled}
          onFocus={() => {
            focusedRef.current = true;
          }}
          onBlur={() => {
            focusedRef.current = false;
            flushChange();
          }}
          onChange={(event) => {
            const cleaned = event.target.value.replace(/[^0-9.\-]/g, '');
            setLocal(cleaned);
            commitChange(rowId as BombiixRowId, field as string, cleaned);
          }}
        />
        <span className="cag-affix cag-affix-suffix">%</span>
      </div>
      {calc !== undefined && calc !== null ? <span className="text-nowrap ml-1">{`(${currencySymbol} ${roundOff(calc || 0, 2)})`}</span> : null}
    </div>
  );
}

const RETAIL_AMOUNT_COLUMNS = ['estimatedRetailValue', 'estimatedProfitPerUnit'];

const truncateToTwoDecimals = (value: unknown): unknown => {
  if (typeof value === 'string' && value.includes('.')) {
    const parts = value.split('.');
    if (parts[1] && parts[1].length > 2) return `${parts[0]}.${parts[1].substring(0, 2)}`;
  }
  return value;
};

export interface LandedInformationTableProps {
  dcIndex: number;
  distributionCentre?: DistributionCentre | undefined;
  showNoBrandWarning?: boolean | undefined;
  fullView?: boolean;
  headerControls?: { title?: string; isFull: boolean; toggleFull: () => void } | undefined;
}

export function LandedInformationTable({
  dcIndex,
  distributionCentre,
  showNoBrandWarning,
  fullView = false,
  headerControls,
}: LandedInformationTableProps): React.JSX.Element | null {
  const {
    landedInfoStates,
    setLandedInfoStates,
    productCostingstate,
    grandTotalState,
    internalState,
    detailedCostingState,
    reRenderLandedInfo,
    setReRenderLandedInfo,
    automatedFreight,
  } = useProductCostingState();
  const { calculateDetailCosting } = useDetailedCostingHooks();

  const { t } = useTranslation('common');
  const { t: tCost } = useTranslation('costing');
  const lengthLabel = useLengthLabel('Length');
  const {
    newProductCostingData: { costingTemplateDetail, productExchnageRate, isRefreshed, productCostingDetail },
    UserDetails: { userInfo, isSupplierLoggedIn },
    SystemMastersDetails: { subBrandList },
    BOMConfigDetails: { productDetails },
  } = useSelector((reduxState: LandedRootState) => reduxState);

  const dcState = dcIndex >= 0 && landedInfoStates[dcIndex] ? landedInfoStates[dcIndex] : null;
  /* The fallback stands in for "this DC has no state yet"; only landedInfoArr and the exchange
     block are read off it, so the partial literal is asserted rather than filled out. */
  const landedInfoState: LandedInfoStateEntry =
    dcState || ({ landedInfo: { landedInfoArr: [] }, landedExchangeRateInformation: {} } as unknown as LandedInfoStateEntry);
  const landedExchangeRateInformation = landedInfoState.landedExchangeRateInformation || {};
  const { primaryCurrValue } = internalState;

  const landedInfoRef = useRef<LandedInfoStateEntry>(landedInfoState);
  const isProcessingRef = useRef<boolean>(false);
  const landedExchangeRateRef = useRef(landedExchangeRateInformation);
  useEffect(() => {
    landedExchangeRateRef.current = landedExchangeRateInformation;
  });
  const { calculateProfit, grandTotalLandedInfoCalculation } = useDetailedPageHook();
  /* The hook's row/template interfaces are not exported; borrowing them from its own signatures
     keeps these casts pinned to the real contract instead of a copy that could drift. */
  type ProfitRows = Parameters<typeof calculateProfit>[0];
  type ProfitTemplate = Parameters<typeof calculateProfit>[1];
  type ComboGroups = Parameters<typeof grandTotalLandedInfoCalculation>[0];
  const {
    productInfo: { currency },
    isBreakdown,
    isGlobalUrl,
    landedInformationColumns,
  } = productCostingstate;
  const { setRefreshCostingTEmplateTable } = useRefreshHooks();
  const prevIsRefreshedRef = useRef(isRefreshed);
  const { grandTotal } = grandTotalState;

  const { isDisabled, hideLengthCol, hideColourCol, hideSizeCol, isSupplier, onLoadDisable, statusState } = useShowHideSectionHooks();
  const {
    landedInfo: { landedInfoArr },
  } = landedInfoState;

  useEffect(() => {
    landedInfoRef.current = landedInfoState;
  }, [landedInfoState]);

  const renderInfoComponent = Array.isArray(landedInfoArr) && landedInfoArr.length > 0;

  const dcCurrency = distributionCentre?.currency || userInfo?.companyCurrency;

  /**
   * Resolves this distribution centre's default exchange rate from the exchangeRate master data.
   * Looks up the combination of the DC's own currency (defaultCurrency) against the supplier
   * currency, so each DC reflects its own rate. Falls back to 1 when no matching combination
   * exists in the master, and to the global primary value only while the master list is still
   * loading (kept distinct so a missing combination shows 1 rather than another DC's rate).
   */
  const dcDefaultCurrValue = useMemo(() => {
    const supplierCurrency = landedExchangeRateInformation?.supplierCurrency || currency;
    return resolveDcDefaultCurrValue(productExchnageRate?.exchangeRateList, dcCurrency, supplierCurrency ?? undefined, primaryCurrValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productExchnageRate, dcCurrency, currency, landedExchangeRateInformation?.supplierCurrency, primaryCurrValue]);

  const getDisplayValue = (val: string): React.ReactNode => {
    if (val === 'supplierCurrency') {
      return landedExchangeRateInformation?.supplierCurrency ? landedExchangeRateInformation?.supplierCurrency : currency;
    }
    if (val === 'companyCurrency') {
      return landedExchangeRateInformation?.companyCurrency ? landedExchangeRateInformation?.companyCurrency : dcCurrency;
    }
    const otherVal =
      productExchnageRate?.defaultCurrencyValue && productExchnageRate?.defaultCurrencyValue !== '' ? productExchnageRate?.defaultCurrencyValue : 1;
    return landedExchangeRateInformation?.companyCurrency ? landedExchangeRateInformation?.companyCurrencyValue : otherVal;
  };

  const setDcLandedInfoState = (updater: (prev: LandedInfoStateEntry) => LandedInfoStateEntry): void => {
    if (dcIndex < 0) return;
    setLandedInfoStates((prev) => {
      const copy = [...prev];
      copy[dcIndex] = updater(copy[dcIndex] as LandedInfoStateEntry);
      return copy;
    });
  };

  useEffect(() => {
    const callFn = async (gTotal: unknown[]): Promise<void> => {
      const exchangeInfo = landedExchangeRateRef.current;
      const currencyValue = exchangeInfo?.manualAdded ? exchangeInfo?.exchangedRate ?? 1 : dcDefaultCurrValue ?? 1;
      setLandedInfomationCalculation(gTotal, currencyValue);
    };
    const shouldRun =
      ((isArray(grandTotal) && grandTotal?.length > 0 && (landedInfoArr?.length ?? 0) > 0) || (isRefreshed && prevIsRefreshedRef.current === false)) &&
      !isProcessingRef.current &&
      !reRenderLandedInfo;
    if (shouldRun) {
      callFn(grandTotal);
    }
    prevIsRefreshedRef.current = isRefreshed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grandTotal, isRefreshed, primaryCurrValue, dcDefaultCurrValue]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (reRenderLandedInfo && isArray(grandTotal) && grandTotal?.length > 0 && !isProcessingRef.current) {
      isProcessingRef.current = true;
      const exchangeInfo = landedExchangeRateRef.current;
      const currencyValue = exchangeInfo?.manualAdded ? exchangeInfo?.exchangedRate ?? 1 : dcDefaultCurrValue ?? 1;
      setLandedInfomationCalculation(grandTotal, currencyValue);
    } else if (reRenderLandedInfo && !isArray(grandTotal)) {
      timeoutId = setTimeout(() => {
        setReRenderLandedInfo(false);
        isProcessingRef.current = false;
      }, 500);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reRenderLandedInfo, grandTotal]);

  const changeSwitch = async (checked: boolean): Promise<void> => {
    const newRate = dcDefaultCurrValue || 1;
    setDcLandedInfoState((prev) => ({
      ...prev,
      landedExchangeRateInformation: {
        ...prev.landedExchangeRateInformation,
        manualAdded: !!checked,
        exchangedRate: newRate,
      },
    }));
    if (isArray(grandTotal) && grandTotal.length > 0) {
      await setLandedInfomationCalculation(grandTotal, newRate);
    }
  };

  const getValue = (): string | number | undefined => {
    if (landedExchangeRateInformation?.manualAdded) {
      return landedExchangeRateInformation?.exchangedRate;
    }
    return dcDefaultCurrValue;
  };

  const setLandedCurrencyVal = async (rate: string): Promise<void> => {
    setDcLandedInfoState((prev) => ({
      ...prev,
      landedExchangeRateInformation: {
        ...prev.landedExchangeRateInformation,
        exchangedRate: rate,
      },
    }));
    const updatedExchangeInfo = {
      ...landedExchangeRateInformation,
      exchangedRate: rate,
    };
    if (dcIndex === 0) {
      await calculateDetailCosting(detailedCostingState, rate, false, currency, updatedExchangeInfo);
    }
    if (isArray(grandTotal) && grandTotal.length > 0) {
      await setLandedInfomationCalculation(grandTotal, rate);
    }
  };

  const setLandedInfomationCalculation = async (gTotal: unknown[], exchangedRate: unknown): Promise<void> => {
    try {
      let copyState: LandedInfoStateEntry = cloneDeep(landedInfoState);
      if (isRefreshed) {
        const colourWayToggleCopy = productCostingDetail?.colourWayToggle;
        const sizeLengthToggleCopy = productCostingDetail?.sizeLengthToggle;
        copyState = (await setRefreshCostingTEmplateTable(Boolean(colourWayToggleCopy), Boolean(sizeLengthToggleCopy), dcIndex)) as LandedInfoStateEntry;
      }
      const companyCurrencyValue = landedExchangeRateInformation?.companyCurrencyValue;
      const updatedLandedInfo = await grandTotalLandedInfoCalculation(
        gTotal as ComboGroups,
        copyState?.landedInfo?.landedInfoArr as ProfitRows,
        exchangedRate,
        companyCurrencyValue,
      );
      const updateArray = await calculateProfit(updatedLandedInfo, costingTemplateDetail as ProfitTemplate, undefined, {
        primaryCurrencyValue: exchangedRate,
        secondaryCurrencyValue: companyCurrencyValue,
        dcCurrency: landedExchangeRateInformation?.companyCurrency || dcCurrency,
      });
      set(copyState, landedInfoFieldPath().LANDED_INFO_DATA, updateArray);
      setDcLandedInfoState((prev) => ({ ...prev, landedInfo: copyState.landedInfo }));
    } catch (error) {
      console.error('Error in landed information calculation:', error);
    } finally {
      isProcessingRef.current = false;
      if (reRenderLandedInfo) {
        setReRenderLandedInfo(false);
      }
    }
  };

  /**
   * Resolves the effective exchange-rate override for this distribution centre.
   * Mirrors the rate logic used on load (manual rate when the per-DC toggle is on,
   * otherwise the global primary currency value) so every recalculation stays
   * consistent with the DC's own exchange-rate input field.
   */
  const getDcExchangeOverride = (): { primaryCurrencyValue: unknown; secondaryCurrencyValue: unknown; dcCurrency: string | undefined } => {
    const exchangeInfo = landedExchangeRateRef.current;
    return {
      primaryCurrencyValue: exchangeInfo?.manualAdded ? exchangeInfo?.exchangedRate ?? 1 : dcDefaultCurrValue ?? 1,
      secondaryCurrencyValue: exchangeInfo?.companyCurrencyValue,
      dcCurrency: exchangeInfo?.companyCurrency || dcCurrency,
    };
  };

  const handleDynamicInputChange = async (val: unknown, index: number, chIndex: number): Promise<void> => {
    const cloneState: LandedInfoStateEntry = cloneDeep(landedInfoRef.current);
    set(cloneState, landedInfoFieldPath(index, chIndex).LANDED_LANDEDINFO_DYNAMIC_COL_VALUE, val);
    if (index >= 0) {
      const profit = await calculateProfit(
        cloneState?.landedInfo?.landedInfoArr as ProfitRows,
        costingTemplateDetail as ProfitTemplate,
        undefined,
        getDcExchangeOverride(),
      );
      set(cloneState, landedInfoFieldPath().LANDED_INFO_DATA, profit);
    }
    landedInfoRef.current = cloneState;
    setDcLandedInfoState((prev) => ({ ...prev, landedInfo: cloneState.landedInfo }));
  };

  const handleDefaultInputChange = async (val: unknown, index: number, aliasName: string): Promise<void> => {
    const cloneState: LandedInfoStateEntry = cloneDeep(landedInfoRef.current);
    set(cloneState, landedInfoFieldPath(index, undefined, aliasName).LANDED_DEFAULT_COL_VALUE, val);
    if (index >= 0) {
      const profit = await calculateProfit(
        cloneState?.landedInfo?.landedInfoArr as ProfitRows,
        costingTemplateDetail as ProfitTemplate,
        undefined,
        getDcExchangeOverride(),
      );
      set(cloneState, landedInfoFieldPath().LANDED_INFO_DATA, profit);
    }
    landedInfoRef.current = cloneState;
    setDcLandedInfoState((prev) => ({ ...prev, landedInfo: cloneState.landedInfo }));
  };

  const handleBottomInputChange = async (val: unknown, index: number, aliasName: string): Promise<void> => {
    const cloneState: LandedInfoStateEntry = cloneDeep(landedInfoRef.current);
    set(cloneState, landedInfoFieldPath(index, undefined, aliasName).LANDED_BOTTOM_COL_VALUE, val);
    if (index >= 0) {
      const profit = await calculateProfit(
        cloneState?.landedInfo?.landedInfoArr as ProfitRows,
        costingTemplateDetail as ProfitTemplate,
        undefined,
        getDcExchangeOverride(),
      );
      set(cloneState, landedInfoFieldPath().LANDED_INFO_DATA, profit);
    }
    landedInfoRef.current = cloneState;
    setDcLandedInfoState((prev) => ({ ...prev, landedInfo: cloneState.landedInfo }));
  };

  const handleFreightToggle = (index: number, checked: unknown): void => {
    const cloneState: LandedInfoStateEntry = cloneDeep(landedInfoRef.current);
    const targetRow = cloneState?.landedInfo?.landedInfoArr?.[index];
    if (!targetRow) return;
    targetRow.freightInputEnabled = checked;
    landedInfoRef.current = cloneState;
    setDcLandedInfoState((prev) => ({ ...prev, landedInfo: cloneState.landedInfo }));
  };

  const companyCurrency = useMemo(() => {
    return dcCurrency || landedExchangeRateInformation?.companyCurrency || '';
  }, [dcCurrency, landedExchangeRateInformation?.companyCurrency]);

  /**
   * Resolves the sub-brand currency symbol for the product's sub-brand, mirroring the
   * getRetailExRate lookup in useDetailedPageHook (subBrandList matched by subBrandId).
   * Returns an empty string when no sub-brand currency resolves so callers can fall back
   * to the company currency symbol for the retail amount columns.
   */
  const subBrandCurrencySymbol = useMemo(() => {
    const subBrandId = (productDetails?.subBrandId as { _id?: string } | undefined)?._id || productDetails?.subBrandId;
    const subBrandCurrency = subBrandList?.find((sb) => sb?._id === subBrandId)?.currency;
    return subBrandCurrency ? checkCurrency(subBrandCurrency, undefined, true) : '';
  }, [productDetails?.subBrandId, subBrandList]);

  if (isSupplierLoggedIn || isGlobalUrl) return null;

  const landedInfoColumns = useMemo<LandedTemplateColumn[]>(() => {
    if (landedInformationColumns?.length && productCostingDetail) {
      return (landedInformationColumns as LandedTemplateColumn[]) || [];
    }
    if (!productCostingDetail) {
      return (internalState?.currSelectedTemplateDetails?.landedInfo as LandedTemplateColumn[] | undefined)?.filter((col) => col?.isActive) || [];
    }
    return [];
  }, [internalState?.currSelectedTemplateDetails?.landedInfo, productCostingDetail, landedInformationColumns]);

  const landedRowData = useMemo<LandedGridRow[]>(() => {
    if (!renderInfoComponent || !Array.isArray(landedInfoArr)) return [];
    return landedInfoArr.map((row, i) => {
      const data = row as LandedDataRow;
      return {
        __cagId: (data?._id as string | undefined) || `idx_${i}`,
        data,
        distributionName: distributionCentre?.distributionName || '',
        colourway: data?.colourWay?.name || data?.colourway?.name,
        size: isBreakdown ? Array.isArray(data?.sizeRange) && shortenStringWithTooltip(data?.sizeRange?.map((item) => item).join(' - '), 15) : data?.size || '',
        length: isBreakdown
          ? Array.isArray(data?.lengthRange) && shortenStringWithTooltip(data?.lengthRange?.map((item) => item?.name).join(' - '), 15)
          : data?.length?.name || '',
      };
    });
  }, [renderInfoComponent, landedInfoArr, isBreakdown, distributionCentre]);

  const onRowChange = useCallback(
    (rowId: BombiixRowId, field: string, value: unknown) => {
      const index = landedRowData.findIndex((row) => row.__cagId === rowId);
      if (index < 0) return;
      if (field === 'freightInputEnabled') {
        handleFreightToggle(index, value);
        return;
      }
      const val = truncateToTwoDecimals(value);
      if (field.startsWith('landedInfo_')) {
        handleDynamicInputChange(val, index, Number(field.slice('landedInfo_'.length)));
      } else if (field.startsWith('default_')) {
        handleDefaultInputChange(val, index, field.slice('default_'.length));
      } else if (field.startsWith('bottom_')) {
        handleBottomInputChange(val, index, field.slice('bottom_'.length));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [landedRowData],
  );

  const handleFillCells = async (changes: BombiixFillBatchItem<LandedGridRow>[]): Promise<void> => {
    if (!Array.isArray(changes) || changes.length === 0) return;
    const cloneState: LandedInfoStateEntry = cloneDeep(landedInfoRef.current);
    let touched = false;

    changes.forEach((change) => {
      const index = landedRowData.findIndex((row) => row.__cagId === change.rowId);
      if (index < 0 || change.field === 'freightInputEnabled') return;
      const val = truncateToTwoDecimals(change.newValue);
      if (change.field.startsWith('landedInfo_')) {
        set(cloneState, landedInfoFieldPath(index, Number(change.field.slice('landedInfo_'.length))).LANDED_LANDEDINFO_DYNAMIC_COL_VALUE, val);
        touched = true;
      } else if (change.field.startsWith('default_')) {
        set(cloneState, landedInfoFieldPath(index, undefined, change.field.slice('default_'.length)).LANDED_DEFAULT_COL_VALUE, val);
        touched = true;
      } else if (change.field.startsWith('bottom_')) {
        set(cloneState, landedInfoFieldPath(index, undefined, change.field.slice('bottom_'.length)).LANDED_BOTTOM_COL_VALUE, val);
        touched = true;
      }
    });

    if (!touched) return;
    const profit = await calculateProfit(
      cloneState?.landedInfo?.landedInfoArr as ProfitRows,
      costingTemplateDetail as ProfitTemplate,
      undefined,
      getDcExchangeOverride(),
    );
    set(cloneState, landedInfoFieldPath().LANDED_INFO_DATA, profit);
    landedInfoRef.current = cloneState;
    setDcLandedInfoState((prev) => ({ ...prev, landedInfo: cloneState.landedInfo }));
  };

  const landedColumnDefs = useMemo<BombiixColumnDef<LandedGridRow>[]>(() => {
    const cols: BombiixColumnDef<LandedGridRow>[] = [];
    const cur = checkCurrency(companyCurrency, undefined, true);
    const retailCur = subBrandCurrencySymbol || cur;

    const fillGetter =
      (scalarFn: (row: LandedGridRow | undefined) => unknown) =>
      (params: ValueGetterParams<LandedGridRow>): unknown => {
        const row = params?.data;
        const colField = params?.colDef?.field;
        if (row && colField && Object.prototype.hasOwnProperty.call(row, colField)) return (row as Record<string, unknown>)[colField];
        return scalarFn(row);
      };

    if (!showNoBrandWarning && distributionCentre?.distributionName) {
      cols.push({
        colId: 'distributionName',
        headerName: 'Distribution Centre',
        cellRenderer: LandedNodeCell,
        width: 167,
        sortable: false,
        cellProps: { accessor: (row: LandedGridRow) => row.distributionName },
      });
    }
    if (hideColourCol) {
      cols.push({
        colId: 'colourway',
        headerName: 'Colourway',
        cellRenderer: LandedNodeCell,
        width: 150,
        sortable: false,
        cellProps: { accessor: (row: LandedGridRow) => row.colourway },
      });
    }
    if (hideSizeCol) {
      cols.push({
        colId: 'size',
        headerName: 'Size',
        cellRenderer: LandedNodeCell,
        width: 150,
        sortable: false,
        cellProps: { accessor: (row: LandedGridRow) => row.size },
      });
    }
    if (hideLengthCol) {
      cols.push({
        colId: 'length',
        headerName: lengthLabel,
        cellRenderer: LandedNodeCell,
        width: 150,
        sortable: false,
        cellProps: { accessor: (row: LandedGridRow) => row.length },
      });
    }

    (costingTemplateDetail?.topColumnArr || []).forEach((col) => {
      if (!['Price Per Unit (Converted)', 'Total (Converted)'].includes(col?.title as string)) return;
      cols.push({
        colId: `top_${col?.aliasName}`,
        headerName: (col?.aliasDisplayName || col?.title) as string,
        cellRenderer: LandedNodeCell,
        width: 150,
        sortable: false,
        cellProps: {
          accessor: (row: LandedGridRow) => (
            <>
              <span className="force-ltr">{cur} </span>
              <span>{(row.data?.[col?.aliasName as string] || '0') as React.ReactNode}</span>
            </>
          ),
        },
      });
    });

    (landedInfoColumns || []).forEach((col, k) => {
      if (!col?.isActive) return;
      const isPercent = col?.type === 'percentage';
      cols.push({
        colId: `landedInfo_${k}`,
        field: `landedInfo_${k}`,
        headerName: `${col?.aliasDisplayName || col?.title}${col?.type?.includes('percentage') ? ' (%)' : ''}`,
        cellRenderer: isPercent ? LandedPercentInputCell : LandedAmountInputCell,
        width: 180,
        sortable: false,
        fillable: true,
        valueGetter: fillGetter((row) => row?.data?.landedInfo?.[k]?.tempValue),
        getCellDisabled: () => statusState,
        cellProps: isPercent
          ? {
              accessor: (row: LandedGridRow) => row.data?.landedInfo?.[k]?.tempValue,
              calcAccessor: (row: LandedGridRow) => row.data?.landedInfo?.[k]?.calculatedValue,
            }
          : { accessor: (row: LandedGridRow) => row.data?.landedInfo?.[k]?.tempValue, narrow: true },
      });
    });

    (costingTemplateDetail?.defaultColumns || []).forEach((col) => {
      const aliasName = col?.aliasName as string;
      const headerName = (col?.aliasDisplayName || col?.title) as string;
      if (col?.type === 'percentage' && col?.isDefault && !['licenseRoyaltyFee', 'likenessRoyaltyFee'].includes(aliasName)) {
        cols.push({
          colId: `default_${aliasName}`,
          field: `default_${aliasName}`,
          headerName,
          cellRenderer: LandedPercentInputCell,
          width: 180,
          sortable: false,
          fillable: true,
          valueGetter: fillGetter((row) => row?.data?.[aliasName]),
          getCellDisabled: (row: LandedGridRow) => statusState || (aliasName === 'freightPercentage' && !row?.data?.freightInputEnabled),
          cellProps: {
            accessor: (row: LandedGridRow) => row.data?.[aliasName],
            calcAccessor: (row: LandedGridRow) => row.data?.[`${aliasName}CalculatedValue`],
          },
        });
      } else if (aliasName === 'agentsFee') {
        cols.push({
          colId: `default_${aliasName}`,
          headerName,
          cellRenderer: LandedNodeCell,
          width: 150,
          sortable: false,
          cellProps: {
            accessor: (row: LandedGridRow) => (
              <>
                <span className="force-ltr">{cur} </span>
                {roundOff(row.data?.[`${aliasName}CalculatedValue`], 2)}
              </>
            ),
          },
        });
      } else if (['licenseRoyaltyFee', 'likenessRoyaltyFee'].includes(aliasName)) {
        cols.push({
          colId: `default_${aliasName}`,
          headerName,
          cellRenderer: LandedNodeCell,
          width: 150,
          sortable: false,
          cellProps: {
            accessor: (row: LandedGridRow) => (
              <>
                <span className="force-ltr">{cur} </span>
                {roundOff(row.data?.[`${aliasName}CalculatedValue`], 2)} {`(${row.data?.[aliasName] || '0'} %)`}
              </>
            ),
          },
        });
      } else if (aliasName === 'freightValue' && col?.type === 'amount') {
        cols.push({
          colId: `default_${aliasName}`,
          field: `default_${aliasName}`,
          headerName,
          cellRenderer: LandedFreightAmountCell,
          width: 200,
          sortable: false,
          fillable: true,
          valueGetter: fillGetter((row) => row?.data?.[aliasName]),
          getCellDisabled: (row: LandedGridRow) => statusState || !row?.data?.freightInputEnabled,
          cellProps: {
            accessor: (row: LandedGridRow) => row.data?.[aliasName],
            freightEnabledAccessor: (row: LandedGridRow) => row.data?.freightInputEnabled,
            toggleDisabled: statusState,
            dcIndex,
          },
        });
      } else if (aliasName === FREIGHT_AUTOMATED_ALIAS) {
        const serverReason = automatedFreight && automatedFreight.status !== 'OK' ? tCost(`AUTOMATED_FREIGHT.${automatedFreight.status}`) : '';
        const reasonFor = (row: LandedGridRow): string => {
          const issue = row?.data?.freightAutomatedIssue;
          if (serverReason || !issue) return serverReason;
          return tCost(`AUTOMATED_FREIGHT.${issue}`, { from: automatedFreight?.currency ?? '', to: distributionCentre?.currency ?? '' });
        };
        cols.push({
          colId: `default_${aliasName}`,
          headerName,
          cellRenderer: LandedNodeCell,
          width: 150,
          sortable: false,
          cellProps: {
            accessor: (row: LandedGridRow) => {
              const amount = (
                <>
                  <span className="force-ltr">{cur} </span>
                  {roundOff(row.data?.[aliasName], 2)}
                </>
              );
              const unavailableReason = reasonFor(row);
              return unavailableReason ? (
                <span className="d-inline-flex align-items-center" data-testid="freight-automated-unavailable">
                  {amount}
                  <CustomTooltip tooltipText={unavailableReason}>
                    <span className="d-inline-flex ml-1" aria-label={unavailableReason}>
                      <InfoIcon size={FREIGHT_INFO_ICON_SIZE} />
                    </span>
                  </CustomTooltip>
                </span>
              ) : (
                amount
              );
            },
          },
        });
      } else {
        cols.push({
          colId: `default_${aliasName}`,
          headerName,
          cellRenderer: LandedNodeCell,
          width: 150,
          sortable: false,
          cellProps: { accessor: (row: LandedGridRow) => row.data?.[aliasName] || '0' },
        });
      }
    });

    (costingTemplateDetail?.bottomColumnArr || []).forEach((col) => {
      const aliasName = col?.aliasName as string;
      const headerName = (col?.aliasDisplayName || col?.title) as string;
      if (['sellingPriceGross', 'estimatedRetailValue'].includes(aliasName) && col?.isEditable) {
        cols.push({
          colId: `bottom_${aliasName}`,
          field: `bottom_${aliasName}`,
          headerName,
          cellRenderer: LandedAmountInputCell,
          width: 150,
          sortable: false,
          fillable: true,
          valueGetter: fillGetter((row) => row?.data?.[aliasName]),
          getCellDisabled: () => statusState,
          cellProps: { accessor: (row: LandedGridRow) => row.data?.[aliasName], currencySymbol: aliasName === 'estimatedRetailValue' ? retailCur : cur },
        });
      } else if (col?.isEditable && ['vat', 'customerDiscount'].includes(aliasName)) {
        cols.push({
          colId: `bottom_${aliasName}`,
          field: `bottom_${aliasName}`,
          headerName,
          cellRenderer: LandedPercentInputCell,
          width: 180,
          sortable: false,
          fillable: true,
          valueGetter: fillGetter((row) => row?.data?.[aliasName]),
          getCellDisabled: () => statusState,
          cellProps: {
            accessor: (row: LandedGridRow) => row.data?.[aliasName],
            calcAccessor: (row: LandedGridRow) => row.data?.[`${aliasName}CalculatedValue`],
          },
        });
      } else if (['marginPerUnit', 'retailMarginPerUnit'].includes(aliasName)) {
        cols.push({
          colId: `bottom_${aliasName}`,
          headerName,
          cellRenderer: LandedNodeCell,
          width: 150,
          sortable: false,
          cellProps: { accessor: (row: LandedGridRow) => (row.data?.[aliasName] === '--' ? '--' : `${roundOff(row.data?.[aliasName] || '0', 2)} %`) },
        });
      } else {
        const columnCur = RETAIL_AMOUNT_COLUMNS.includes(aliasName) ? retailCur : cur;
        cols.push({
          colId: `bottom_${aliasName}`,
          headerName,
          cellRenderer: LandedNodeCell,
          width: 150,
          sortable: false,
          cellProps: {
            accessor: (row: LandedGridRow) =>
              row.data?.[aliasName] === '--' ? (
                '--'
              ) : (
                <>
                  <span className="force-ltr">{columnCur} </span>
                  {roundOff(row.data?.[aliasName] || '0', 2)}
                </>
              ),
          },
        });
      }
    });

    return cols.map((column) => ({ valueGetter: (params: ValueGetterParams<LandedGridRow>) => params?.data?.data, ...column }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showNoBrandWarning,
    distributionCentre,
    hideColourCol,
    hideSizeCol,
    hideLengthCol,
    costingTemplateDetail,
    landedInfoColumns,
    companyCurrency,
    subBrandCurrencySymbol,
    statusState,
    dcIndex,
    lengthLabel,
    automatedFreight,
    tCost,
  ]);

  return (
    <div className="landed-container">
      <div className="d-flex align-items-center flex-wrap mb-16">
        {headerControls?.title && <p className="product-cp-title mb-0 mr-3 text-nowrap">{headerControls.title}</p>}
        {!showNoBrandWarning && (
          <div className="d-flex flex-wrap align-items-center small ml-0 my-2 my-md-0">
            <div className="switch-common d-flex design-switch">
              <input
                type="checkbox"
                className="form-check-input mt-0"
                id={`checkbox_landed_${dcIndex}`}
                onChange={(event) => changeSwitch(event.target.checked)}
                checked={landedExchangeRateInformation?.manualAdded || false}
                disabled={isSupplier || onLoadDisable || isDisabled || statusState}
              />
              <label
                className="text-nowrap design-title text-black mt-0 mb-0"
                htmlFor={`checkbox_landed_${dcIndex}`}
                title={t('DEVLOPMENT_ORDER.COSTING_DETAILS.MANUAL_EXCHANGE_RATE')}
              />
            </div>
            <span className="mr-1 text-nowrap">
              {t('DEVLOPMENT_ORDER.COSTING_DETAILS.EXCHANGE_RATE')} {getDisplayValue('defaultCurrencyValue')} {getDisplayValue('companyCurrency')} =
            </span>
            <input
              disabled={!landedExchangeRateInformation?.manualAdded || isDisabled || statusState}
              key={`exchange_${dcIndex}`}
              name="royaltyFee"
              value={getValue()}
              type="number"
              onChange={(event) => setLandedCurrencyVal(event.target.value)}
              className="form-control exchange-rate-input inWid9"
            />
            <span className="ml-1 mr-3">{getDisplayValue('supplierCurrency')}</span>

            {landedExchangeRateInformation?.manualAdded && landedExchangeRateInformation?.updatedBy && (
              <span>
                ({t('DEVLOPMENT_ORDER.COSTING_DETAILS.MANUAL_AMENDED')}{' '}
                <span>
                  {landedExchangeRateInformation?.updatedBy} on
                  <span className="ml-1">
                    {moment(landedExchangeRateInformation?.updatedAt).format('DD-MMM-YYYY')} at{' '}
                    {moment(landedExchangeRateInformation?.updatedAt).format('HH:mm')}
                  </span>
                </span>
                )
              </span>
            )}
          </div>
        )}
        <div className="d-flex align-items-center ml-auto f-14">
          {!isSupplier ? (
            <span className="d-flex align-items-center text-nowrap">
              <span className="supplier-icon-container ">
                <InactiveIcon size={ICON_SIZE_SM} />
              </span>
              {t('DEVLOPMENT_ORDER.COSTING_DETAILS.NOT_VISIBLE_SUPLIER')}
            </span>
          ) : null}
          {headerControls && (
            <button
              type="button"
              /* Same affordance as CostingCardShell's own toggle — this card renders its header
                 itself, so it has to carry the primitive rather than inherit it. */
              className="bombiix-icon-container bombiix-icon-container--solid-dark ml-3"
              aria-label={headerControls.isFull ? 'Collapse' : 'Expand'}
              title={headerControls.isFull ? 'Collapse' : 'Expand'}
              onClick={headerControls.toggleFull}
            >
              {headerControls.isFull ? <CollapseIcon size={ICON_SIZE_CARD} /> : <ExpandIcon size={ICON_SIZE_CARD} />}
            </button>
          )}
        </div>
      </div>
      <div id="page">
        <div id="landedInfo" className={`table-responsive ${fullView ? '' : ' table-max-h-50'}`}>
          {showNoBrandWarning ? (
            <p className="text-center py-3 mb-0">{t('DEVLOPMENT_ORDER.COSTING_DETAILS.NO_BRAND_WARNING', { ns: 'costing' })}</p>
          ) : (
            <BombiixAGGrid<LandedGridRow>
              gridOptions={{ suppressMovableColumns: true }}
              revealHeaderControlsOnHover
              sortable={false}
              showColumnMenu={false}
              enableFillHandle
              onFillCells={handleFillCells}
              columnDefs={landedColumnDefs}
              rowData={landedRowData}
              getRowId={(row) => row.__cagId}
              onRowChange={onRowChange}
              currencySymbol={checkCurrency(companyCurrency, undefined, true)}
              {...(fullView ? {} : { height: '100%' })}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default LandedInformationTable;

const FREIGHT_INFO_ICON_SIZE = 16;
